import PropTypes from 'prop-types'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNotificationContext, useUserContext } from '../../context'
import { mutation } from '../../graphql'
import { sendSlackMessage } from '../../helpers'
import { useSetState } from '../../hooks/use-set-state'
import Button from '../tailwind/Button'
import Modal from '../tailwind/Modal'
import StarRating from './star-rating'

const ProfileFeedbackModal = ({
  open = false,
  setOpen = () => undefined,
  context = 'UNSET'
}) => {
  const { t } = useTranslation()
  const { success } = useNotificationContext()
  const {
    activeSpace,
    refetchActiveSpace,
    cognitoUser: { username }
  } = useUserContext()

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [message, setMessage] = useState('')
  const [rating, setRating] = useSetState({
    understandable: null,
    helpful: null
  })

  const handleClose = ({ status }) => {
    // ___
    // if status is only seen, we can later remove seen
    // from the employ space (customer-dashboard-meta)
    // to display the modal for this employee again

    const { id: active_space_id, customer_dashboard_meta } = activeSpace

    mutation({
      mutation: 'updateSpace',
      input: {
        id: active_space_id,
        customer_dashboard_meta: JSON.stringify([
          ...customer_dashboard_meta,
          { event: `FEEDBACK_${context}`, status }
        ])
      }
    }).then(refetchActiveSpace)

    setOpen(false)
  }

  const handleSubmit = async () => {
    setIsSubmitting(true)

    const input = {
      input: JSON.stringify({ ...rating, message }),
      context,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    }

    await mutation({
      mutation: 'createFeedback',
      input,
      options: { disableLastUpdatedBy: true }
    })

    const slackMessage = [
      'PDF-FEEDBACK',
      context,
      `username: ${username}`,
      JSON.stringify({ ...rating, message })
    ].join(' / ')

    sendSlackMessage('kunden-controlling', slackMessage)
    success(t('profile_feedback_modal.success_message'))
    setIsSubmitting(false)
    handleClose({ status: 'SUBMITTED' })
  }

  if (context === null) return null

  const profileTitle = t(`profile_feedback_modal.${context}`)

  return (
    <Modal
      open={open}
      setOpen={() => handleClose({ status: 'SEEN' })}
      size='2xl'
    >
      <div className='px-5 py-5'>
        <h1 className='text-center text-2xl font-bold text-gray-900'>
          {t('profile_feedback_modal.title')}
        </h1>
        <p className='mt-2 text-center text-sm text-gray-900'>
          {t('profile_feedback_modal.description', { profileTitle })}
        </p>
        <h3 className='mt-6 text-lg font-medium text-gray-900'>
          {t('profile_feedback_modal.question_1', { profileTitle })}
        </h3>
        <StarRating
          className='mt-1.5'
          value={rating.understandable}
          onChange={(value) => setRating({ understandable: value })}
          starSize='size-8'
        />
        <h3 className='mt-6 text-lg font-medium text-gray-900'>
          {t('profile_feedback_modal.question_2', { profileTitle })}
        </h3>
        <StarRating
          className='mt-1.5'
          value={rating.helpful}
          onChange={(value) => setRating({ helpful: value })}
          starSize='size-8'
        />
        <div className='mt-6 flex items-end gap-x-1'>
          <h3 className='text-lg font-medium text-gray-900'>
            {t('profile_feedback_modal.question_3')}
          </h3>
          <span className='pb-1 text-xs italic text-gray-700'>(optional)</span>
        </div>
        <textarea
          name='feedback'
          id='feedback'
          rows={4}
          value={message}
          onChange={(event) => setMessage(event.target.value)}
          className='mt-1.5 block w-full rounded-md border-gray-300 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500'
        />
        <div className='mt-8 flex justify-end'>
          <Button.PrimaryXL
            onClick={handleSubmit}
            isLoading={isSubmitting}
            disabled={rating.understandable === null || rating.helpful === null}
            text={t('profile_feedback_modal.submit_action')}
          />
        </div>
      </div>
    </Modal>
  )
}

ProfileFeedbackModal.propTypes = {
  open: PropTypes.bool,
  setOpen: PropTypes.func,
  context: PropTypes.string
}

export default ProfileFeedbackModal
